import { useState, useEffect, useContext } from 'react';
import { omit } from 'lodash';
import BusinessPage from 'src/pages/business';
import Step from 'src/components/Step';
import StepperContext from 'src/store/stepper-context';
import ToastContext from 'src/store/toast-context';
import BusinessContext from 'src/store/business-context';
import { useAPI } from 'src/services/api';
import { useLoaded } from 'src/store/load-context';
import businessInfoSchema from 'src/components/schemas/businessInfoSchema';
import ContactModal from 'src/components/modal/ContactModal';
import getNatureofBus from 'src/util/getNatureOfBus';
import getUpdatedValue from 'src/util/getUpdatedValue';
import handleNull from 'src/util/handleNull';
import { getCityState, getCityZip } from 'src/util/parseAddress';
import ownershipTypes from 'src/constants/ownershipTypes';
import homeBusiness from 'src/constants/homeBusiness';
import formatDateToIso from 'src/util/formatDateToIso';
import AuthContext from 'src/store/auth-context';
import { AppContext } from 'src/store/app-context';
import shouldBypassItemization from 'src/util/shouldBypassItemization';
const BusinessStep = (props) => {
  const api = useAPI();
  const { account } = useContext(AuthContext);
  const { shouldSkipToLastStep, setShouldSkipToLastStep } = useContext(AppContext);
  const { nextStep } = useContext(StepperContext);
  const { handleError } = useContext(ToastContext);
  const { setContacts, sameForMail, contactAgreement } = useContext(BusinessContext);
  const { setLoaded } = useLoaded();
  const [natureOfBusOptions, setNatureOfBusOptions] = useState([]);
  const [initialValues, setInitialValues] = useState({
    busName: '',
    emailAddr: '',
    natureOfBus: '',
    ownershipType: '',
    ownerChg: '',
    homebiz: '',
    dateOpenedLoc: '',
    busLoc: '',
    busCity: '',
    busZip: '',
    mailAddr: '',
    mailCity: '',
    mailZip: '',
    naicsCode: '',
    dbaName: '',
    dbaSameAsPrimary: false,
    sameForMail,
    contactAgreement,
  });

  const fetchInitialValues = async () => {
    setLoaded(false);
    const { data } = handleNull(await api.get('/business/details'));

    const { businessInfo, updatedBusinessInfo, businessContacts, natureOfBusOptions } = data;

    data.businessContacts = data.businessContacts.filter((contact, index) => {
      if (index === 0) return true;
      return (
        contact.recno !== data.businessContacts.at(index - 1).recno || contact.recno === '-New-'
      );
    });

    setShouldSkipToLastStep(shouldBypassItemization(account, businessInfo));

    const newData = {
      busName: getUpdatedValue(businessInfo.businessName, updatedBusinessInfo.busName),
      emailAddr: getUpdatedValue(businessInfo.bizEmail, updatedBusinessInfo.emailAddr),
      natureOfBus: getUpdatedValue(
        getNatureofBus(businessInfo.nature),
        updatedBusinessInfo.natureOfBus
      ),
      ownershipType: getUpdatedValue(
        ownershipTypes[businessInfo.ownerType] ?? '',
        updatedBusinessInfo.ownershipType
      ),
      homebiz: getUpdatedValue(
        homeBusiness[businessInfo.homebusiness] ?? '',
        updatedBusinessInfo.homebiz
      ),
      dateOpenedLoc: getUpdatedValue(
        businessInfo.locOpenDate ? formatDateToIso(new Date(businessInfo.locOpenDate)) : '',
        updatedBusinessInfo.dateOpenedLoc
          ? formatDateToIso(new Date(updatedBusinessInfo.dateOpenedLoc))
          : ''
      ),
      busLoc: getUpdatedValue(businessInfo.businessAddr, updatedBusinessInfo.busLoc),
      busCity: getUpdatedValue(
        getCityState(businessInfo.businessCityline),
        updatedBusinessInfo.busCity
      ),
      busZip: getUpdatedValue(
        getCityZip(businessInfo.businessCityline),
        updatedBusinessInfo.busZip
      ),

      mailAddr: getUpdatedValue(businessInfo.streetAddr, updatedBusinessInfo.mailAddr),
      mailCity: getUpdatedValue(
        getCityState(businessInfo.streetCityline),
        updatedBusinessInfo.mailCity
      ),
      mailZip: getUpdatedValue(
        getCityZip(businessInfo.streetCityline),
        updatedBusinessInfo.mailZip
      ),
      naicsCode: getUpdatedValue(businessInfo.naicsCode, updatedBusinessInfo.naicsCode),
      dbaName: getUpdatedValue(businessInfo.dbaName, updatedBusinessInfo.dbaName),
    };

    setContacts(businessContacts);
    setNatureOfBusOptions(natureOfBusOptions);
    setInitialValues({ ...initialValues, ...newData });
    setLoaded(true);
  };

  const submitFormData = async (values) => {
    try {
      let formData = { ...omit(values, ['sameForMail', 'dbaSameAsPrimary']) };
      if (values.sameForMail) {
        formData = {
          ...formData,
          mailAddr: values.busLoc,
          mailCity: values.busCity,
          mailZip: values.busZip,
        };
      }

      if (formData?.contactAgreement) {
        delete formData.contactAgreement;
      }

      await api.patch(`/business`, formData);
      setInitialValues(formData);

      //if the business was closed or sold iin a previous year we should skip 3 steps to the final step else just skip 1 to the next step
      nextStep(shouldSkipToLastStep ? 3 : 1);
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => {
    fetchInitialValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Step
      component={<BusinessPage natureOfBusOptions={natureOfBusOptions} />}
      modals={[<ContactModal key="contactModal" />]}
      onComplete={submitFormData}
      initialValues={initialValues}
      schema={businessInfoSchema}
      path={['/business']}
      defaultPath={'/business'}
      {...props}
    />
  );
};

export default BusinessStep;
